import type { FC, PropsWithChildren } from 'react'
import React, { useRef, useCallback, useState, useReducer, useEffect, useMemo } from 'react'
import styled from '@emotion/styled'
import { css, useTheme } from '@emotion/react'
import { useRouter } from 'next/router'
import { useIsFetching } from '@tanstack/react-query'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import CheckboxField from '@segment/matcha/components/CheckboxField'
import Divider from '@segment/matcha/components/Divider'

import GettingStarted from '@segment/templates/GettingStarted'
import Footer from '@segment/templates/Footer'
import NavigationV2 from '@segment/templates/Navigation2'

import { trackCatalogSearch } from '@segment/tracking/scripts'

import Layout from '@segment/next-shared/layout'

import Hero from './components/Hero'
import DesktopCategoriesList from './components/DesktopCategoriesList'
import MobileCategoriesList from './components/MobileCategoriesList'
import GroupedGrid from './components/GroupedGrid'
import CategoryGrid from './components/CategoryGrid'
import LoadingOverlay from './components/LoadingOverlay'
import SearchGrid from './components/SearchGrid'

import useGroupedGrid from './components/GroupedGrid/useGroupedGrid'
import useSearchGrid from './components/SearchGrid/useSearchGrid'
import useCategoryGrid from './components/CategoryGrid/useCategoryGrid'

import ogImagePng from './assets/OgCatalog.png'

export interface CatalogFilteringState {
  query: string
  destination: boolean
  source: boolean
}

export interface Category {
  name: string
  slug: string
}
export interface CatalogViewProps {
  categoryId: string | null
  filteringState: CatalogFilteringState
  categories: Category[]
  onCategoryIdChange?: (nextCategoryId: string) => void
  onTypeChange?: (nextTypeState: CatalogFilteringState) => void
}

type Action = { type: `source` } | { type: `destination` }

const typeReducer = (state: CatalogFilteringState, action: Action) => {
  switch (action.type) {
    case `source`:
      return { ...state, source: !state.source }
    case `destination`:
      return { ...state, destination: !state.destination }
    default:
      return state
  }
}

const useIsChangingCategoryRoute = () => {
  const [isChangingCategoryRoute, setIsChangingCategoryRoute] = useState(false)
  const router = useRouter()
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (url.match(/^\/catalog/)) {
        setIsChangingCategoryRoute(true)
      }
    }

    const handleRouteChangeFinalized = () => {
      setIsChangingCategoryRoute(false)
    }

    router.events.on(`routeChangeStart`, handleRouteChange)
    router.events.on(`routeChangeComplete`, handleRouteChangeFinalized)
    router.events.on(`routeChangeError`, handleRouteChangeFinalized)

    // If the component is unmounted, unsubscribe
    // from the event with the `off` method:
    return () => {
      router.events.off(`routeChangeStart`, handleRouteChange)
      router.events.off(`routeChangeComplete`, handleRouteChangeFinalized)
      router.events.off(`routeChangeError`, handleRouteChangeFinalized)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return isChangingCategoryRoute
}

const CatalogView: FC<PropsWithChildren<CatalogViewProps>> = ({
  categoryId,
  filteringState: initialFilteringState,
  categories,
}) => {
  const theme = useTheme()
  const [query, setQuery] = useState(``)
  const heroRef = useRef<HTMLDivElement>(null)
  const [isLoadingCategoryRouteData, setIsLoadingCategoryRouteData] = useState(false)
  const isSearching = Boolean(query.trim())
  const normalizedCategoryId = categoryId === `all` ? null : categoryId
  const category = categories.find((c) => c.slug === categoryId)
  const isInCategory = normalizedCategoryId !== null && normalizedCategoryId !== undefined
  const [filteringState, dispatch] = useReducer(typeReducer, initialFilteringState)
  const isChangingCategoryRoute = useIsChangingCategoryRoute()

  useEffect(() => {
    if (query.length) {
      trackCatalogSearch(query)
    }
  }, [query])

  const property = useMemo(() => {
    if (!isInCategory && !isSearching) {
      return `GroupedGrid`
    }

    if (isInCategory && category && !isSearching) {
      return `CategoryGrid`
    }

    if (isSearching) {
      return `SearchGrid`
    }
  }, [isInCategory, isSearching, category])

  const groupedGrid = useGroupedGrid({ filteringState })
  const searchGrid = useSearchGrid({ filteringState, category, query })
  const categoryGrid = useCategoryGrid({ filteringState, category, query })
  const ongoingFetches = useIsFetching([`catalog`])
  const isFetching = ongoingFetches > 0
  const showLoadingState = isFetching || isLoadingCategoryRouteData || isChangingCategoryRoute

  const showFooter = useMemo(() => {
    if (!isInCategory && !isSearching) {
      return Boolean(!groupedGrid.hasNextPage)
    }

    if (isInCategory && category && !isSearching) {
      return Boolean(!categoryGrid.hasNextPage)
    }

    if (isSearching) {
      return Boolean(!searchGrid.hasNextPage)
    }

    return false
  }, [isInCategory, isSearching, category, categoryGrid, groupedGrid, searchGrid])

  const router = useRouter()

  const scrollToTop = () => {
    const positionTop = heroRef.current ? heroRef.current?.getBoundingClientRect().height : 0
    // 96 it is a navbar height
    window.scrollTo({
      top: positionTop - 96,
      behavior: `smooth`,
    })
  }

  const onCategoryChange = useCallback(
    (value: string | number | null) => {
      setIsLoadingCategoryRouteData(true)
      scrollToTop()

      if (value === `all`) {
        router.push(`/catalog/`, undefined, { scroll: false }).then(() => {
          setIsLoadingCategoryRouteData(false)
        })
      } else {
        router.push(`/catalog/${value}`, undefined, { scroll: false }).then(() => {
          setIsLoadingCategoryRouteData(false)
        })
      }
    },
    [router]
  )

  const pageDescription = `One API unlocks ${process.env.INTEGRATION_COUNT}+ tools. All the best apps for analytics, advertising, chat, data-warehousing and more. Twilio Segment’s catalog has every tool you need to grow.`

  return (
    <AppearanceProvider appearance="twilioLight">
      <Layout
        pageTitle="Integration Catalog | Twilio Segment"
        pageDescription={pageDescription}
        pageOG={ogImagePng}
        canonicalUrl="https://segment.com/catalog/"
        themeColor={theme.palette.twilio.gray[10]}
      >
        <NavigationV2 />
        <Main>
          <Hero ref={heroRef} query={query} onQueryChange={setQuery} isFetching={isFetching} />

          <Content>
            <Sidebar bottomSpace={Boolean(showFooter)}>
              <FiltersAside>
                <MobileCategoriesSlot>
                  <FilterLabel>Filter by</FilterLabel>

                  <MobileCategoriesList
                    categoryId={categoryId}
                    categories={categories}
                    onCategoryChange={onCategoryChange}
                  />
                </MobileCategoriesSlot>
                <TypesSlot>
                  <DesktopFilterTitle>Types</DesktopFilterTitle>
                  <TypeCheckbox
                    checked={filteringState.source}
                    label="Sources"
                    name="sources-checkbox"
                    onChange={() => dispatch({ type: `source` })}
                  />
                  <TypeCheckbox
                    checked={filteringState.destination}
                    label="Destinations"
                    name="destinations-checkbox"
                    onChange={() => dispatch({ type: `destination` })}
                  />
                </TypesSlot>
                <DesktopCategoriesSlot>
                  <DesktopCategoriesList
                    categoryId={categoryId}
                    categories={categories}
                    onCategoryChange={onCategoryChange}
                  />
                </DesktopCategoriesSlot>
              </FiltersAside>
            </Sidebar>
            <MobileDivider />
            <ScrollToHash id="grid">
              {property === `GroupedGrid` && (
                <GroupedGrid
                  onCategoryChange={onCategoryChange}
                  nextPageTrigger={groupedGrid.nextPageTrigger}
                  flattenedPages={groupedGrid.groupedQueryDataFlattenedPages}
                  isEmpty={groupedGrid.isEmpty}
                  destination={filteringState.destination}
                  source={filteringState.source}
                  isLoading={showLoadingState}
                />
              )}
              {property === `CategoryGrid` && (
                <CategoryGrid
                  category={category}
                  flattenedPages={categoryGrid.categoryQueryDataFlattenedPages}
                  nextPageTrigger={categoryGrid.nextPageTrigger}
                  isEmpty={categoryGrid.isEmpty}
                  isLoading={showLoadingState}
                />
              )}
              {property === `SearchGrid` && (
                <SearchGrid
                  category={category}
                  query={query}
                  onCategoryChange={onCategoryChange}
                  isEmpty={searchGrid.isEmpty}
                  flattenedPagesCategorySearch={searchGrid.flattenedPagesCategorySearch}
                  flattenedPagesAllCategoriesSearch={searchGrid.flattenedPagesAllCategoriesSearch}
                  nextPageTrigger={searchGrid.nextPageTrigger}
                  emptyHits={searchGrid.emptyHits}
                  isLoading={showLoadingState}
                />
              )}
            </ScrollToHash>
            <LoadingOverlay
              data-testid="loading-overlay"
              aria-busy={showLoadingState}
              data-loading={showLoadingState}
              isFetching={showLoadingState}
            />
          </Content>
          {showFooter && <GettingStarted />}
        </Main>
      </Layout>
      {showFooter && <Footer />}
    </AppearanceProvider>
  )
}

const ScrollToHash = styled.div`
  padding-top: 96px;
  margin-top: -96px;
`

const Main = styled.main`
  background-color: ${({ theme }) => theme.palette.twilio.gray[10]};
`

const DesktopFilterTitle = styled.h4`
  ${({ theme }) => theme.typography.buttonLarge}
  display: none;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    display: block;
  }
`

const FilterLabel = styled.p`
  ${({ theme }) => theme.typography.textBoldSmall}
  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    display: none;
  }
`

interface SidebarProps {
  bottomSpace: boolean
}
const Sidebar = styled.div<SidebarProps>`
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    ${({ bottomSpace }) =>
      bottomSpace &&
      css`
        padding-bottom: 200px;
      `}
  }
  position: relative;
`

const TypeCheckbox = styled(CheckboxField)`
  display: block;
`

const FiltersAside = styled.aside`
  top: 0;
  position: sticky;
  display: grid;
  gap: 24px;
  grid-template-columns: auto;
  align-items: center;
  align-content: flex-start;
  padding: 20px 24px;

  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    grid-template-columns: auto auto;
    padding: 40px;
    gap: 56px;
  }

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    grid-template-columns: auto;
    align-items: initial;
    padding: 120px 0 0 0;
    margin-top: -40px;
  }
`

const MobileDivider = styled(Divider)`
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    display: none;
  }
`

const FiltersSlot = styled.div`
  display: grid;
  gap: 24px;
`

const TypesSlot = styled(FiltersSlot)`
  display: flex;
  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    grid-auto-flow: column;
    display: grid;
  }
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    grid-auto-flow: row;
  }
`

const MobileCategoriesSlot = styled(FiltersSlot)`
  display: grid;
  grid-auto-flow: column;
  gap: 24px;
  justify-content: stretch;
  align-items: center;
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    display: none;
  }
`

const DesktopCategoriesSlot = styled(FiltersSlot)`
  display: none;
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    display: grid;
  }
`

const Content = styled.div`
  position: relative;
  width: 100%;
  max-width: 1520px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: auto;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    padding: 0 40px;
    gap: 60px;
    grid-template-columns: 256px minmax(auto, 1054px);
  }
`

export default CatalogView
